/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { slackDirectoryConnectionsSync } from "../funcs/slackDirectoryConnectionsSync.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SyncResponseBody } from "../models/components/syncresponsebody.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SyncSlackDirectoryRequest,
  SyncSlackDirectorySecurity,
} from "../models/operations/syncslackdirectory.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SyncSlackDirectoryMutationVariables = {
  request: SyncSlackDirectoryRequest;
  security?: SyncSlackDirectorySecurity | undefined;
  options?: RequestOptions;
};

export type SyncSlackDirectoryMutationData = SyncResponseBody;

export type SyncSlackDirectoryMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * sync slackDirectoryConnections
 *
 * @remarks
 * Request a complete Slack workspace directory sync. Concurrent requests join the running sync.
 */
export function useSyncSlackDirectoryMutation(
  options?: MutationHookOptions<
    SyncSlackDirectoryMutationData,
    SyncSlackDirectoryMutationError,
    SyncSlackDirectoryMutationVariables
  >,
): UseMutationResult<
  SyncSlackDirectoryMutationData,
  SyncSlackDirectoryMutationError,
  SyncSlackDirectoryMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSyncSlackDirectoryMutation(client, options),
    ...options,
  });
}

export function mutationKeySyncSlackDirectory(): MutationKey {
  return ["@gram/client", "slackDirectoryConnections", "sync"];
}

export function buildSyncSlackDirectoryMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SyncSlackDirectoryMutationVariables,
  ) => Promise<SyncSlackDirectoryMutationData>;
} {
  return {
    mutationKey: mutationKeySyncSlackDirectory(),
    mutationFn: function syncSlackDirectoryMutationFn({
      request,
      security,
      options,
    }): Promise<SyncSlackDirectoryMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(slackDirectoryConnectionsSync(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
